<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import NewHeader from '~/components/NewHeader.vue';
import NewFooter from '~/components/NewFooter.vue';
import AiToolLanding from '~/components/AiToolLanding.vue';
import EditorCTA from '~/components/EditorCTA.vue';
import api from '~/api';
import axios from 'axios';

const authStore = useAuthStore();
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();

useHead({
  title: () => t('aiPose.seo.title'),
  meta: [
    { name: 'description', content: () => t('aiPose.seo.description') },
  ],
});

// 多段式落地页配置（参考 ai-undress 结构）；文案走 i18n（aiPose.*）
const ASSETS = 'https://cdn.treekee.com/ai-undress/images/ai-sex-pose-generator';
const config = computed(() => ({
  hero: {
    title: t('aiPose.hero.title'),
    description: t('aiPose.hero.description'),
    videoSrc: `${ASSETS}/hero-pose.mp4`,
    videoPoster: `${ASSETS}/hero-pose.webp`,
  },
  stats: {
    items: [
      { value: '10+', label: t('aiPose.stats.poseStyles') },
      { value: 'HD/FHD', label: t('aiPose.stats.outputQuality') },
      { value: '<60s', label: t('aiPose.stats.generationTime') },
      { value: '100%', label: t('aiPose.stats.privacySafe') },
    ],
  },
  gallery: {
    title: t('aiPose.gallery.title'),
    description: t('aiPose.gallery.description'),
    items: [
      { src: `${ASSETS}/example-pose-01.webp`, alt: t('aiPose.gallery.alt1') },
      { src: `${ASSETS}/example-pose-02.webp`, alt: t('aiPose.gallery.alt2') },
      { src: `${ASSETS}/example-pose-03.webp`, alt: t('aiPose.gallery.alt3') },
    ],
  },
  imageFeatures: {
    title: t('aiPose.features.title'),
    description: t('aiPose.features.description'),
    items: [
      {
        title: t('aiPose.features.f1Title'),
        description: t('aiPose.features.f1Desc'),
        video: { src: `${ASSETS}/feature-pose-gen.mp4`, poster: `${ASSETS}/feature-pose-gen.webp` },
      },
      {
        title: t('aiPose.features.f2Title'),
        description: t('aiPose.features.f2Desc'),
        video: { src: `${ASSETS}/feature-styles.mp4`, poster: `${ASSETS}/feature-styles.webp` },
      },
      {
        title: t('aiPose.features.f3Title'),
        description: t('aiPose.features.f3Desc'),
        video: { src: `${ASSETS}/feature-quality.mp4`, poster: `${ASSETS}/feature-quality.webp` },
      },
    ],
  },
  usage: {
    title: t('aiPose.usage.title'),
    steps: [
      { title: t('aiPose.usage.s1Title'), description: t('aiPose.usage.s1Desc') },
      { title: t('aiPose.usage.s2Title'), description: t('aiPose.usage.s2Desc') },
      { title: t('aiPose.usage.s3Title'), description: t('aiPose.usage.s3Desc') },
    ],
  },
  faq: {
    title: t('aiPose.faq.title'),
    items: [
      { question: t('aiPose.faq.q1'), answer: t('aiPose.faq.a1') },
      { question: t('aiPose.faq.q2'), answer: t('aiPose.faq.a2') },
      { question: t('aiPose.faq.q3'), answer: t('aiPose.faq.a3') },
      { question: t('aiPose.faq.q4'), answer: t('aiPose.faq.a4') },
    ],
  },
  cta: {
    title: t('aiPose.cta.title'),
    description: t('aiPose.cta.description'),
    buttonText: t('aiPose.cta.button'),
    buttonHref: '#hero',
  },
}));

// ===== 交互逻辑 =====
const POLL_INTERVAL = 60000;
const MAX_POLLS = 5;

const prompt = ref('');
const sourceImages = ref<string[]>([]);
const quality = ref<'HD' | 'FHD'>('HD');
const submitting = ref(false);
const polling = ref(false);
const pollTimer = ref<any>(null);
const resultUrl = ref('');
const errorMsg = ref('');
const orderId = ref('');
const taskId = ref('');
const isUploading = ref(false);

const cost = computed(() => quality.value === 'FHD' ? 12 : 8);
const canSubmit = computed(() =>
  !submitting.value && !polling.value && prompt.value.trim().length >= 3 && sourceImages.value.length > 0,
);

async function uploadFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  if (!files.length) return;
  if (sourceImages.value.length + files.length > 3) {
    alert('Up to 3 reference images.');
    return;
  }
  isUploading.value = true;
  try {
    for (const file of files) {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const sign = await api.getSignUrl(ext);
      await axios.put(sign.sign_url, file, {
        headers: { 'Content-Type': file.type || getMimeType(file.name) },
      });
      sourceImages.value.push((sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`);
    }
  } catch (e) {
    console.error('upload failed', e);
    alert('Upload failed');
  } finally {
    isUploading.value = false;
    input.value = '';
  }
}

async function submit() {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/ai-pose'));
    return;
  }
  errorMsg.value = '';
  resultUrl.value = '';
  submitting.value = true;
  try {
    const r = await api.createSexPose({
      prompt: prompt.value.trim(),
      image_urls: sourceImages.value,
      quality: quality.value,
    });
    orderId.value = r.order_id;
    taskId.value = r.task_id;
    submitting.value = false;
    polling.value = true;
    startPolling();
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || 'Submission failed';
  }
}

function startPolling() {
  let attempts = 0;
  pollTimer.value = setInterval(async () => {
    attempts++;
    try {
      const r = await api.fetchSexPoseRes(orderId.value, taskId.value);
      if (r.task_status === 1) {
        resultUrl.value = r.url;
        stopPolling();
      } else if (r.task_status === -1) {
        errorMsg.value = r.message || 'Generation failed';
        stopPolling();
      } else if (attempts >= MAX_POLLS) {
        errorMsg.value = 'Timeout — task may still be processing';
        stopPolling();
      }
    } catch {/* ignore */}
  }, POLL_INTERVAL);
}
function stopPolling() {
  polling.value = false;
  if (pollTimer.value) { clearInterval(pollTimer.value); pollTimer.value = null; }
}
function reset() { resultUrl.value = ''; errorMsg.value = ''; taskId.value = ''; orderId.value = ''; }
onUnmounted(() => stopPolling());
</script>

<template>
  <div class="bg-neutral-950 min-h-screen">
    <NewHeader />
    <AiToolLanding :config="config">
      <template #form>
        <EditorCTA tool="sex-pose" />
        <div v-if="false" class="form-card">
          <div class="field">
            <label>Reference Images <span class="hint">(up to 3)</span></label>
            <div class="thumbs">
              <div v-for="(url, i) in sourceImages" :key="url" class="thumb">
                <img :src="url" alt="" referrerpolicy="no-referrer" />
                <button class="thumb-x" @click="sourceImages.splice(i, 1)">×</button>
              </div>
              <label v-if="sourceImages.length < 3" class="thumb-upload">
                <input type="file" accept="image/*" multiple class="hidden" :disabled="isUploading" @change="uploadFile" />
                <span v-if="isUploading">…</span><span v-else>+</span>
              </label>
            </div>
          </div>
          <div class="field">
            <label>Prompt <span class="req">*</span></label>
            <textarea v-model="prompt" rows="3" placeholder="Describe the pose / scene…" />
          </div>
          <div class="field">
            <label>Quality</label>
            <div class="seg">
              <button :class="{ active: quality === 'HD' }" @click="quality = 'HD'">HD · 8 credits</button>
              <button :class="{ active: quality === 'FHD' }" @click="quality = 'FHD'">FHD · 12 credits</button>
            </div>
          </div>

          <div v-if="resultUrl" class="result">
            <img :src="resultUrl" alt="Result" referrerpolicy="no-referrer" />
            <div class="actions">
              <a :href="resultUrl" target="_blank" rel="noopener" class="btn-secondary">Open</a>
              <button class="btn-secondary" @click="reset">New</button>
            </div>
          </div>
          <div v-else-if="polling" class="result-placeholder">
            <div class="spinner" /><p>Generating…</p>
          </div>

          <button class="cta-btn" :disabled="!canSubmit" @click="submit">
            <span v-if="submitting">Submitting…</span>
            <span v-else-if="polling">Generating… (30s–2min)</span>
            <span v-else>Generate · {{ cost }} credits</span>
          </button>
          <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
        </div>
      </template>
    </AiToolLanding>
    <NewFooter />
  </div>
</template>

<style scoped lang="scss">
.form-card {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 space-y-4 text-white;
}
.field { @apply space-y-2; }
.field label { @apply text-sm font-medium block; }
.hint { @apply text-xs text-neutral-500; }
.req { @apply text-amber-400; }
.field textarea, .field input {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500;
}
.thumbs { @apply flex flex-wrap gap-2; }
.thumb { @apply relative size-20 overflow-hidden rounded-lg border border-neutral-800; }
.thumb img { @apply size-full object-cover; }
.thumb-x { @apply absolute right-0.5 top-0.5 size-5 rounded-full bg-black/60 text-white text-sm leading-none; }
.thumb-upload {
  @apply flex size-20 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-neutral-700
    text-white/50 hover:bg-neutral-900 text-3xl;
}
.seg { @apply flex gap-2; }
.seg button {
  @apply flex-1 rounded-full border border-neutral-700 bg-neutral-950/60 px-4 py-2 text-sm transition;
  &.active { @apply border-amber-500 bg-amber-500/10 text-amber-300; }
}
.cta-btn {
  @apply w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.err { @apply text-sm text-red-400; }
.result { @apply space-y-2; }
.result img { @apply w-full rounded-xl object-contain max-h-[400px]; }
.actions { @apply flex gap-2 justify-center; }
.btn-secondary {
  @apply inline-flex items-center justify-center rounded-full border border-neutral-700 px-4 py-2 text-sm hover:bg-neutral-800 cursor-pointer text-white;
}
.result-placeholder { @apply text-center text-neutral-500 py-6 space-y-2; }
.spinner { @apply mx-auto size-8 rounded-full border-2 border-neutral-700 border-t-amber-500 animate-spin; }
</style>
